import { Link, useParams, Navigate } from 'react-router-dom';
import { PageHero } from '../components/ui/PageHero';
import { AnimatedCard } from '../components/ui/AnimatedCard';
import { InquiryForm } from '../components/ui/InquiryForm';
import { CTAButton } from '../components/ui/CTAButton';
import { RelatedLinks } from '../components/ui/RelatedLinks';
import { getProduct, PURCHASE_PROCESS } from '../constants/content';
import { inquiryPath, productPath } from '../constants/navigation';

const GENERAL_INQUIRY = {
  title: '通用购买咨询',
  shortDesc: '不确定选哪款？告诉我们您的用途与预算，我们将为您推荐最合适的产品方案。',
};

export default function InquiryDetailPage() {
  const { slug } = useParams();
  const isGeneral = slug === 'general';
  const product = isGeneral ? null : getProduct(slug);

  if (!isGeneral && !product) {
    return <Navigate to="/inquiry" replace />;
  }

  const title = isGeneral ? GENERAL_INQUIRY.title : `${product.title} · 购买咨询`;
  const subtitle = isGeneral ? GENERAL_INQUIRY.shortDesc : product.shortDesc;

  return (
    <>
      <PageHero
        title={title}
        subtitle={subtitle}
        breadcrumb={[
          { label: '首页', path: '/' },
          { label: '购买咨询', path: '/inquiry' },
          { label: isGeneral ? '通用咨询' : product.title },
        ]}
      />

      <section className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 max-w-6xl mx-auto">
          <div className="lg:col-span-2">
            <AnimatedCard className="bg-white/5 rounded-2xl p-8 border border-purple-500/20">
              <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
                <i className="fa fa-edit text-secondary" />
                填写咨询信息
              </h2>
              <InquiryForm
                productName={product?.title || ''}
                subject={`Ameowzing 购买咨询 - ${product?.title || '通用'}`}
                showProductField={isGeneral}
              />
            </AnimatedCard>
          </div>

          <div className="space-y-6">
            {!isGeneral && product && (
              <AnimatedCard className="bg-white/5 rounded-2xl p-6 border border-purple-500/20">
                <img
                  src={product.image}
                  alt={product.title}
                  className="w-full max-w-[200px] mx-auto mb-4 object-contain"
                />
                <h3 className="font-semibold mb-2">{product.title}</h3>
                <p className="text-purple-400 text-sm mb-3">{product.priceNote}</p>
                <p className="text-purple-300 text-xs mb-4">起订：{product.moq}</p>
                <CTAButton to={productPath(product.slug)} variant="ghost" size="sm" icon="fa-info-circle">
                  查看完整产品信息
                </CTAButton>
              </AnimatedCard>
            )}

            <div className="bg-white/5 rounded-2xl p-6 border border-purple-500/20">
              <h3 className="font-semibold text-secondary mb-4">咨询流程</h3>
              <ol className="space-y-3">
                {PURCHASE_PROCESS.map((item) => (
                  <li key={item.step} className="flex gap-3 text-sm">
                    <span className="text-purple-400 shrink-0">{item.step}.</span>
                    <span className="text-purple-200">{item.title}</span>
                  </li>
                ))}
              </ol>
            </div>

            {!isGeneral && product && (
              <div className="bg-white/5 rounded-2xl p-6 border border-purple-500/20">
                <h3 className="font-semibold text-secondary mb-3">适用场景</h3>
                <ul className="space-y-2">
                  {product.scenarios.map((s) => (
                    <li key={s} className="text-purple-300 text-sm flex items-start gap-2">
                      <i className="fa fa-check text-green-400 mt-1 text-xs" />
                      {s}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <RelatedLinks
              links={[
                ...(product
                  ? [{ to: productPath(product.slug), label: '产品详情页' }]
                  : []),
                { to: '/inquiry', label: '返回购买咨询首页' },
                { to: '/contact/gift', label: '礼品批量采购' },
                { to: '/products', label: '浏览全部产品' },
              ]}
            />
          </div>
        </div>

        {!isGeneral && product && (
          <div className="max-w-6xl mx-auto mt-12">
            <h2 className="text-xl font-bold mb-4">同系列其他产品</h2>
            <div className="flex flex-wrap gap-4">
              <Link
                to={inquiryPath('general')}
                className="text-sm text-purple-300 hover:text-secondary"
              >
                通用咨询 →
              </Link>
            </div>
          </div>
        )}
      </section>
    </>
  );
}
